import { Injectable, computed, inject, signal } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs";

import { PermissionsRoleService } from "../http/permissionsRole/permissions-role.service";
import {
  PLATFORM_SURFACE_PERMISSION,
  SURFACES,
  SurfaceDefinition,
  SurfaceId,
  surfaceForUrl
} from "./surface.tokens";
import { SpineAuthorizationService } from "./spine-authorization.service";
import { WardAccessService } from "./ward-access.service";

interface StoredRole {
  _id?: string;
  name?: string;
}

/**
 * LEGACY FALLBACK — used only until `GET /me/authorization` lands.
 *
 * The server decides this properly: `computeSurfaces` grants `learn` on an active enrolment
 * row, `parent` on an active guardianship, and `manage` on holding any manage permission. That
 * is the right rule, because being a learner is a fact about enrolment rather than a job title
 * — a trainer, a marketing head or a support agent who is enrolled in a course is a learner in
 * exactly the same way a student is.
 *
 * Guessing it from a role NAMED `Student` was wrong in both directions: it withheld `/learn`
 * from every enrolled staff member, and it handed `/manage` to any learner whose tenant had
 * renamed the role (a renamed learner satisfies `name !== "Student"` and reads as staff).
 *
 * Kept only because a surface must resolve before the payload arrives, and an empty menu is a
 * worse answer than an approximate one. Once `spine.loaded()` is true this is never consulted.
 */
const LEARNER_ROLE = "Student";

/**
 * Which surfaces an account can enter, and which one it is currently in.
 *
 * Availability comes from the account's memberships and permission union; the active surface
 * comes from the URL. There is no mutable "current role" anywhere in the chain, which is what
 * lets every granted surface stay live at once.
 */
@Injectable({ providedIn: "root" })
export class SurfaceService {
  private readonly router = inject(Router);
  private readonly permissions = inject(PermissionsRoleService);
  private readonly wards = inject(WardAccessService);
  private readonly spine = inject(SpineAuthorizationService);

  private readonly currentUrl = signal<string>(this.router.url);

  readonly active = computed<SurfaceId | null>(() => surfaceForUrl(this.currentUrl()));

  /**
   * Memberships live in storage, which is not reactive. Reading the permission revision gives
   * this something to invalidate on: signing in loads permissions immediately afterwards, so a
   * session established without a page reload still gets a fresh answer.
   */
  readonly available = computed<readonly SurfaceDefinition[]>(() => {
    this.permissions.revision();
    // Reading the spine signals here is what makes this recompute when the payload lands.
    this.spine.loaded();
    return SURFACES.filter((s) => this.isAvailable(s.id));
  });

  readonly hasMultiple = computed<boolean>(() => this.available().length > 1);

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.currentUrl.set(e.urlAfterRedirects));
  }

  /**
   * The server's answer wins once it has one.
   *
   * `platform` is deliberately still local: it is a Recolearn-operator surface gated on a legacy
   * resource, not one of the three tenant surfaces `/me/authorization` reports on.
   */
  isAvailable(id: SurfaceId): boolean {
    if (id === "platform") {
      return this.permissions.isActionAllowed(PLATFORM_SURFACE_PERMISSION, "view") === true;
    }
    // Keyed on the payload, not on `loaded()`: a read that FAILED also settles, and answering
    // "no surface" to a question that was never answered locked every learner out of `/learn`.
    //
    // An EMPTY surface list is the same fact one step further in, and the asymmetry
    // `SpineAuthorizationService.seeded` documents: the request succeeded, so `!== null` does
    // not catch it, but the account simply has no spine membership — the state every legacy
    // administrator is in. No account that can sign in holds zero surfaces, so an empty list is
    // "the spine has no opinion", never "every surface refused". Reading it as a refusal denied
    // `manage` AND `learn` to an unseeded admin, and `AuthGuard` then bounced them to /login —
    // but only from the one route that re-checks mid-session, and only after some other screen
    // had triggered the payload to land. Unseeded means the legacy check alone decides, exactly
    // as it did before any of this existed.
    const answer = this.spine.authorization();
    if (answer !== null && answer.surfaces.length > 0) {
      return answer.surfaces.includes(id);
    }
    switch (id) {
      case "manage":
        return this.isStaffMember();
      case "learn":
        return this.isEnrolledLearner();
      case "parent":
        return this.hasWards();
    }
  }

  /**
   * Every membership counts, so a trainer who is also enrolled still gets `/manage`: one
   * non-learner membership is enough — never a "current" role and never a role *count*.
   * Permissions decide what renders inside the surface, not whether it exists, so a slow or
   * failed permission fetch cannot lock out an admin.
   */
  private isStaffMember(): boolean {
    const roles = this.readJson<StoredRole[]>("currentRole");
    if (!Array.isArray(roles)) {
      return false;
    }
    const isLearner = this.learnerPredicate(roles);
    return roles.some((r) => r.name !== undefined && !isLearner(r.name));
  }

  private isEnrolledLearner(): boolean {
    const roles = this.readJson<StoredRole[]>("currentRole");
    if (Array.isArray(roles)) {
      const isLearner = this.learnerPredicate(roles);
      if (roles.some((r) => r.name !== undefined && isLearner(r.name))) {
        return true;
      }
    }
    const userTag = this.readJson<string>("user_tag");
    return userTag === LEARNER_ROLE;
  }

  /**
   * Which membership names count as the learner role, decided once per read so both surface
   * questions agree.
   *
   * A literal `Student` membership means the wire is in its known shape, and then only that name
   * counts — so a custom role sharing the tenant's learner word is still staff. The tenant term
   * is accepted only when no `Student` membership is present at all: the exact case that would
   * otherwise silently promote a learner to staff.
   */
  private learnerPredicate(roles: StoredRole[]): (name: string) => boolean {
    if (roles.some((r) => r.name === LEARNER_ROLE)) {
      return (name) => name === LEARNER_ROLE;
    }
    const tenantTerm = this.readJson<string>("user_tag");
    if (typeof tenantTerm === "string" && tenantTerm.trim() !== "") {
      return (name) => name === LEARNER_ROLE || name === tenantTerm;
    }
    return (name) => name === LEARNER_ROLE;
  }

  /**
   * A guardianship is a row on the relationship, not a permission and not a membership, so
   * `GET /parent/wards` is the only thing that can answer this. Reading the signal here is
   * what makes `available()` recompute when the probe lands.
   */
  private hasWards(): boolean {
    return this.wards.hasWards();
  }

  private readJson<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? null : (JSON.parse(raw) as T);
    } catch {
      return null;
    }
  }
}
